import './assets/main.css';

import { createAuth0 } from '@auth0/auth0-vue';
import { auth0 } from './auth0.js';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import { defaults } from 'mande';
import PrimeVue from 'primevue/config';
import EtPreset from './theme/etPreset';

import App from './App.vue';
import router from './router';
import { logToBetterStack, heapMB } from './utils/logger';
import { installUsageTracking } from './lib/usage';

defaults.headers['X-Etainabl-App'] = 'readings-ui';

const app = createApp(App);

// Error + diagnostic telemetry via the Sentry SDK, delivered to BetterStack
// (same approach as the main UI — see etainabl-ui/src/main.js). Sentry
// auto-captures uncaught errors and promise rejections, so we don't wire
// window handlers manually.
//
// IMPORTANT: Replay and Profiling (which the main UI enables) are deliberately
// left OFF here. Session Replay continuously buffers the DOM in memory — the
// exact pressure this app is being fixed to avoid on low-memory iOS devices.
Sentry.init({
  app,
  enabled: Boolean(import.meta.env.VITE_APP_BETTERSTACK_DSN),
  dsn: import.meta.env.VITE_APP_BETTERSTACK_DSN || undefined,
  integrations: [Sentry.browserTracingIntegration({ router }), Sentry.httpClientIntegration()],
  // On, this attaches request headers (the Auth0 bearer token) and cookies to failed-request events
  sendDefaultPii: false,
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/[a-z.]*app\.etainabl\.com/,
    /^https:\/\/[a-z-]*api\.etainabl\.com/,
    /^\//
  ],
  tracesSampleRate: 0.1,
  release: 'readings-ui@' + (import.meta.env.VITE_APP_VERSION || 'local'),
  environment: import.meta.env.VITE_APP_ENV,
  beforeSend(event, hint) {
    const error = hint.originalException as Error | undefined;
    // Drop chunk-load failures — transient network/deploy races, not real bugs
    // (mirrors the main UI's beforeSend filter).
    if (error?.message && /loading chunk \S* failed/i.test(error.message)) {
      return null;
    }
    return event;
  }
});

// Reload counter: an iOS WebKit memory kill terminates and reloads the tab, so a
// climbing load count within one tab session is our proxy for the crash
// happening (Safari exposes no heap API on the affected device). sessionStorage
// survives a same-tab reload, so this increments on each jetsam reload.
try {
  const loads = Number(sessionStorage.getItem('rui_tab_loads') || '0') + 1;
  sessionStorage.setItem('rui_tab_loads', String(loads));
  logToBetterStack('app_start', { loadCountThisTabSession: loads, heapMB: heapMB() });
} catch {
  /* storage may be unavailable; ignore */
}

app.use(createPinia());
app.use(router);
installUsageTracking(router);
app.use(auth0);
app.use(PrimeVue, {
  theme: {
    preset: EtPreset,
    options: {
      prefix: 'etn',
      darkModeSelector: 'system',
      cssLayer: false
    }
  },
  pt: {
    select: {
      overlay: ({ instance }: { instance: { $el?: HTMLElement | null } }) => {
        const width = instance?.$el ? `${instance.$el.offsetWidth}px` : undefined;

        return width ? { style: { '--et-select-panel-width': width } } : {};
      }
    }
  }
});

app.mount('#app');
